import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import Header from '../components/Header';
import { EVENT_GENRE_OPTIONS } from '../constants/eventGenres';
import styles from './CreateEventPage.module.css';
import { useAuth } from '../context/AuthContext';

const EventEditPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const [loadingAuth, setLoadingAuth] = useState(true);
  const createEmptyArtist = () => ({ id: '', name: '', description: '', imageFile: null, imagePreview: '', existingImageUrl: '' });

  const [form, setForm] = useState({
    name: '', date: '', end_date: '', open_time: '', start_time: '', end_time: '',
    slug: '', genre: '', address: '', price: '', description: '', contact_info: '', venue_id: '', venue_name: '', fee_bearer: 'organizer',
    event_type: 'paid', donation_enabled: false, donation_start_at: '', donation_end_at: '',
  });
  const [artists, setArtists] = useState([createEmptyArtist()]);
  const [flyers, setFlyers] = useState([]);
  const [existingFlyers, setExistingFlyers] = useState([]);
  const [venueSearch, setVenueSearch] = useState('');
  const [venueResults, setVenueResults] = useState([]);
  const [artistSearch, setArtistSearch] = useState('');
  const [artistResults, setArtistResults] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({});
  const [selectedArtist, setSelectedArtist] = useState(null);
  const [ticketPrices, setTicketPrices] = useState([]);
  const [flyerPreviews, setFlyerPreviews] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState('');
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const normalizeNullableText = (value) => {
    if (value === null || value === undefined) return '';
    const text = String(value);
    if (text.trim().toLowerCase() === 'null') return '';
    return text;
  };

  const humanizeValidationMessage = (key, message) => {
    const msg = String(message || '');
    if (key === 'contact_info') {
      if (msg.includes('required')) {
        return '問い合わせ先は必須項目です。';
      }
      return '問い合わせ先を確認してください。';
    }
    if (/^artist_names\.\d+$/.test(key)) {
      if (msg.includes('required')) {
        return 'アーティスト名は必須項目です。';
      }
      return 'アーティスト名を確認してください。';
    }
    if (/^artist_images\.\d+$/.test(key)) {
      return '未登録アーティストの写真には画像ファイルを指定してください。';
    }
    if (/^flyers\.\d+$/.test(key)) {
      return 'フライヤーには画像ファイルを指定してください。';
    }
    return msg;
  };

  useEffect(() => {
    if (!success) return undefined;
    const timerId = setTimeout(() => {
      navigate('/dashboard');
    }, 2500);

    return () => clearTimeout(timerId);
  }, [success, navigate]);

  const getFieldError = (...names) => {
    for (const name of names) {
      const msg = fieldErrors?.[name]?.[0];
      if (msg) return msg;
    }
    return '';
  };

  const getErrorSummary = () => {
    const messages = Object.values(fieldErrors || {}).flat().map((msg) => String(msg));
    if (messages.length === 0) return '';
    return Array.from(new Set(messages)).join(' / ');
  };

  const clearFieldError = (...names) => {
    setFieldErrors((prev) => {
      const next = { ...prev };
      let changed = false;
      names.forEach((name) => {
        if (next[name]) {
          delete next[name];
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  };

  const isPastEvent = (dateValue, endDateValue) => {
    const referenceDate = endDateValue || dateValue;
    if (!referenceDate) return false;
    return new Date(referenceDate) < new Date(new Date().toDateString());
  };

  const getDisplayValue = (value, fallback = '未入力') => {
    const text = String(value ?? '').trim();
    return text || fallback;
  };

  const getArtistSummary = () => {
    const names = artists
      .map((artist) => String(artist?.name || '').trim())
      .filter(Boolean);
    return names.length > 0 ? names.join(' / ') : '未設定';
  };

  const getTicketSummary = () => {
    if (form.event_type === 'free') {
      return ['無料イベント'];
    }

    const lines = ticketPrices
      .filter((group) => String(group?.type || '').trim() !== '' || (group?.prices || []).length > 0)
      .map((group) => {
        const groupName = getDisplayValue(group?.type, '種別未設定');
        const priceNames = (group?.prices || [])
          .map((price) => {
            const type = getDisplayValue(price?.price_type, '価格タイプ未設定');
            const amount = String(price?.price ?? '').trim();
            return amount !== '' ? `${type} ${amount}円` : type;
          })
          .join(' / ');
        return priceNames ? `${groupName}: ${priceNames}` : groupName;
      });

    return lines.length > 0 ? lines : ['未設定'];
  };

  const getConfirmSections = () => ([
    { label: 'イベント名', value: getDisplayValue(form.name) },
    { label: 'ジャンル', value: getDisplayValue(form.genre) },
    { label: '開催日', value: getDisplayValue(form.date) },
    { label: '終了日', value: getDisplayValue(form.end_date) },
    { label: '開場 / 開演 / 終演', value: `${getDisplayValue(form.open_time)} / ${getDisplayValue(form.start_time)} / ${getDisplayValue(form.end_time)}` },
    { label: '会場', value: getDisplayValue(form.venue_name) },
    { label: '住所', value: getDisplayValue(form.address) },
    { label: '問い合わせ先', value: getDisplayValue(form.contact_info) },
    { label: 'イベント種別', value: form.event_type === 'free' ? '無料' : '有料' },
    { label: '投げ銭', value: form.donation_enabled ? `有効${form.donation_start_at ? `（開始: ${form.donation_start_at}）` : ''}` : '無効' },
    { label: 'チケット種別・料金', value: getTicketSummary().join('\n') },
    { label: '出演アーティスト', value: getArtistSummary() },
    { label: 'フライヤー', value: `${existingFlyers.length}枚` },
  ]);

  const validateBeforePublish = () => {
    setFieldErrors({});
    setSuccess('');

    if (!existingFlyers || existingFlyers.length === 0) {
      setFieldErrors({ flyers: ['フライヤー画像を1枚以上登録してください。'] });
      return false;
    }

    return true;
  };

  const submitPublish = async () => {
    setIsSubmitting(true);
    try {
      const formData = new FormData();
      Object.entries(form).forEach(([key, value]) => {
        if (key === 'price') {
          formData.append(key, value || 0);
        } else {
          formData.append(key, value);
        }
      });
      artists.forEach((artist, i) => {
        formData.append(`artist_ids[${i}]`, artist.id || '');
        formData.append(`artist_names[${i}]`, artist.name);
        formData.append(`artist_descriptions[${i}]`, artist.id ? '' : (artist.description || ''));
        if (!artist.id && artist.imageFile) {
          formData.append(`artist_images[${i}]`, artist.imageFile);
        }
      });
      if (form.event_type !== 'free') {
        ticketPrices.forEach((tp, i) => {
          if (tp.id) formData.append(`price_groups[${i}][id]`, String(tp.id));
          formData.append(`price_groups[${i}][type]`, tp.type);
          formData.append(`price_groups[${i}][price_date]`, tp.price_date);
          formData.append(`price_groups[${i}][price_end_date]`, tp.price_end_date);
          formData.append(`price_groups[${i}][ticket_limit]`, tp.ticket_limit || '');
          tp.prices.forEach((pt, j) => {
            if (pt.id) formData.append(`price_groups[${i}][prices][${j}][id]`, String(pt.id));
            formData.append(`price_groups[${i}][prices][${j}][price_type]`, pt.price_type);
            formData.append(`price_groups[${i}][prices][${j}][price]`, pt.price);
            formData.append(`price_groups[${i}][prices][${j}][ticket_limit]`, pt.ticket_limit || '');
            if (pt.price_type === 'グループ') {
              formData.append(`price_groups[${i}][prices][${j}][min_people]`, pt.min_people || '');
            }
          });
        });
      }
      formData.set('event_type', form.event_type || 'paid');
      formData.set('donation_enabled', form.donation_enabled ? '1' : '0');
      formData.set('donation_start_at', form.donation_start_at || '');
      formData.set('donation_end_at', form.donation_end_at || '');
      await axios.post(`${process.env.REACT_APP_API_URL}/events/${id}?_method=PUT`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      setIsConfirmOpen(false);
      setSuccess('イベント情報を更新しました。ダッシュボードへ遷移します。');
    } catch (err) {
      const data = err?.response?.data;
      if (data?.errors && typeof data.errors === 'object') {
        const normalizedFieldErrors = {};
        Object.entries(data.errors).forEach(([key, value]) => {
          const rawMessages = Array.isArray(value) ? value : [value];
          const messages = rawMessages.map((msg) => humanizeValidationMessage(key, msg));
          normalizedFieldErrors[key] = messages;
        });
        setFieldErrors(normalizedFieldErrors);
      } else if (data?.message) {
        setFieldErrors({ submit: [String(data.message)] });
      } else {
        setFieldErrors({ submit: ['更新に失敗しました。'] });
      }
      setIsConfirmOpen(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    // 編集権限ガード
    const fetchEvent = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/my-events/${id}/status`);
        const eventData = res.data.event;
        // 本人チェック（主催者IDとログインユーザーIDが一致しなければリダイレクト）
        if (!user || eventData.organizer_id !== user.id) {
          navigate('/');
          return;
        }
        setForm({
          ...eventData,
          date:               eventData.date?.slice(0, 10)       || '',
          end_date:           eventData.end_date?.slice(0, 10)   || '',
          open_time:          eventData.open_time?.slice(0, 5)   || '',
          start_time:         eventData.start_time?.slice(0, 5)  || '',
          end_time:           eventData.end_time?.slice(0, 5)    || '',
          genre:              eventData.genre                    || '',
          venue_name:         eventData.venue                    || '',
          description:        normalizeNullableText(eventData.description),
          contact_info:       normalizeNullableText(eventData.contact_info),
          fee_bearer:         eventData.fee_bearer               || 'organizer',
          event_type:         eventData.event_type               || 'paid',
          donation_enabled:   eventData.donation_enabled         ?? false,
          donation_start_at:  eventData.donation_start_at ? String(eventData.donation_start_at).slice(0, 16) : '',
          donation_end_at:    eventData.donation_end_at   ? String(eventData.donation_end_at).slice(0, 16)   : '',
        });
        setArtists((eventData.event_artists || []).map(a => ({
          ...createEmptyArtist(),
          id: a.artist_id || '',
          name: a.artist_name || '',
          description: a.artist_description || '',
          existingImageUrl: a.artist_profile_image_url || '',
        })));
        setExistingFlyers(eventData.flyers || []);
        // 新API: price_groups をそのまま利用
        setTicketPrices((eventData.price_groups || []).map(g => ({
          // 実販売（売上/予約）が1件でもあれば開始済みとみなす
          hasActualSales: (g.prices || []).some((pt) => Number(pt.sold_count || 0) > 0 || Number(pt.reserved_count || 0) > 0),
          id: g.id,
          type: g.type || '',
          price_date: g.price_date || '',
          price_end_date: g.price_end_date || '',
          ticket_limit: g.ticket_limit || '',
          prices: (g.prices || []).map(pt => ({
            id: pt.id,
            price_type: pt.price_type || '',
            price: pt.price || '',
            ticket_limit: pt.ticket_limit || '',
            min_people: pt.min_people || ''
          }))
        })));
      } catch (err) {
        setFieldErrors({ submit: ['イベント情報の取得に失敗しました。'] });
      } finally {
        setLoadingAuth(false);
      }
    };
    if (user) {
      fetchEvent();
    }
  }, [id, user, navigate]);

  useEffect(() => {
    if (!venueSearch || typeof venueSearch !== 'string' || venueSearch.trim() === '') return;
    axios.get(`${process.env.REACT_APP_API_URL}/search-venues?query=${venueSearch}`)
      .then(res => setVenueResults(res.data))
      .catch(() => setVenueResults([]));
  }, [venueSearch]);

  useEffect(() => {
    if (artistSearch.trim() === '') return;
    axios.get(`${process.env.REACT_APP_API_URL}/search-users?role=artist&query=${artistSearch}`)
      .then(res => setArtistResults(res.data))
      .catch(() => setArtistResults([]));
  }, [artistSearch]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'venue_name') {
      setForm(prev => ({ ...prev, venue_name: value, venue_id: '' }));
    } else if (name === 'date' || name === 'end_date') {
      const nextDate = name === 'date' ? value : form.date;
      const nextEndDate = name === 'end_date' ? value : form.end_date;
      const isPast = isPastEvent(nextDate, nextEndDate);
      setForm(prev => ({
        ...prev,
        [name]: value,
        ...(isPast ? { event_type: 'free', donation_enabled: false } : {}),
      }));
    } else {
      setForm(prev => ({ ...prev, [name]: value }));
    }
    clearFieldError(name, 'submit');
  };

  // フライヤー: 追加アップロード（逐次）
  const handleFlyerUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    clearFieldError('flyers');
    for (const file of files) {
      const fd = new FormData();
      fd.append('type', 'flyer');
      fd.append('image', file);
      try {
        const res = await axios.post(`${process.env.REACT_APP_API_URL}/events/${id}/images`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        const newImage = res.data?.image || null;
        if (newImage) setExistingFlyers(prev => [...prev, newImage]);
      } catch (err) {
        console.error('フライヤーアップロード失敗', err);
        alert('フライヤーのアップロードに失敗しました');
      }
    }
    // リセット
    e.target.value = '';
  };

  // フライヤー: 削除
  const handleDeleteFlyer = async (imageId) => {
    if (!window.confirm('このフライヤーを削除しますか？')) return;
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/event-images/${imageId}`);
      setExistingFlyers(prev => prev.filter(f => f.id !== imageId));
    } catch (err) {
      console.error('フライヤー削除失敗', err);
      alert('フライヤーの削除に失敗しました');
    }
  };

  // 価格帯（期間）追加・削除・編集
  const handleTicketPriceChange = (idx, field, value) => {
    setTicketPrices(prices => prices.map((p, i) => i === idx ? { ...p, [field]: value } : p));
  };
  const addTicketPrice = () => {
    setTicketPrices(prices => [...prices, {
      hasActualSales: false,
      type: '',
      price_date: '',
      price_end_date: '',
      ticket_limit: '',
      prices: [ { price_type: '通常', price: '', ticket_limit: '' } ]
    }]);
  };
  const removeTicketPrice = (idx) => {
    setTicketPrices(prices => prices.filter((_, i) => i !== idx));
  };
  // 価格タイプ追加・削除・編集（各期間ごと）
  const handlePriceTypeChange = (periodIdx, priceIdx, field, value) => {
    setTicketPrices(prices => prices.map((p, i) =>
      i === periodIdx
        ? { ...p, prices: p.prices.map((pt, j) => j === priceIdx ? { ...pt, [field]: value } : pt) }
        : p
    ));
  };
  const addPriceType = (periodIdx, price_type) => {
    setTicketPrices(prices => prices.map((p, i) =>
      i === periodIdx
        ? { ...p, prices: [...p.prices, { price_type, price: '', ticket_limit: '', min_people: '' }] }
        : p
    ));
  };
  const removePriceType = (periodIdx, priceIdx) => {
    setTicketPrices(prices => prices.map((p, i) =>
      i === periodIdx
        ? { ...p, prices: p.prices.filter((_, j) => j !== priceIdx) }
        : p
    ));
  };

  const addArtistToList = (artist) => {
    if (!artist) return;
    if (artist.id && artists.some((a) => a.id === artist.id)) {
      return;
    }
    const normalizedArtist = {
      ...createEmptyArtist(),
      ...artist,
    };
    {
      setArtists(prev => {
        const copy = [...prev];
        const emptyIndex = copy.findIndex(a => !a.name || (typeof a.name === 'string' && a.name.trim() === ''));
        if (emptyIndex !== -1) {
          copy[emptyIndex] = normalizedArtist;
        } else {
          copy.push(normalizedArtist);
        }
        return copy;
      });
    }
  };

  const handleArtistValueChange = (index, field, value) => {
    setArtists(prev => prev.map((artist, i) => {
      if (i !== index) return artist;

      if (field === 'name') {
        return {
          ...artist,
          name: value,
          id: '',
          existingImageUrl: artist.id ? '' : artist.existingImageUrl,
        };
      }

      return {
        ...artist,
        [field]: value,
      };
    }));
  };

  const handleArtistImageChange = (index, file) => {
    setArtists(prev => prev.map((artist, i) => {
      if (i !== index) return artist;

      return {
        ...artist,
        imageFile: file || null,
        imagePreview: file ? URL.createObjectURL(file) : '',
      };
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    if (!validateBeforePublish()) {
      return;
    }

    setIsConfirmOpen(true);
  };

  if (!form || loadingAuth) return <p>読み込み中...</p>;

  return (
    <>
      <Header />
      <div className={styles.container}>
        <h2 className={styles.heading}>イベント編集</h2>

        {isConfirmOpen && (
          <div className={styles.confirmOverlay} onClick={() => !isSubmitting && setIsConfirmOpen(false)}>
            <div className={styles.confirmModal} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="イベント更新内容の確認">
              <h3 className={styles.confirmTitle}>更新内容の確認</h3>
              <p className={styles.confirmText}>以下の内容で更新して公開します。内容を確認してください。</p>
              <div className={styles.confirmList}>
                {getConfirmSections().map((section) => (
                  <div key={section.label} className={styles.confirmItem}>
                    <div className={styles.confirmLabel}>{section.label}</div>
                    <div className={styles.confirmValue}>{section.value}</div>
                  </div>
                ))}
              </div>
              <div className={styles.confirmActions}>
                <button type="button" className={styles.secondarySubmitButton} onClick={() => setIsConfirmOpen(false)} disabled={isSubmitting}>戻る</button>
                <button type="button" onClick={submitPublish} disabled={isSubmitting}>{isSubmitting ? '更新中…' : 'この内容で公開する'}</button>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className={styles.form}>
          <fieldset>
            <legend>フライヤー画像</legend>
            {existingFlyers?.length > 0 ? (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, 140px)', gap: 12 }}>
                {existingFlyers.map(img => (
                  <div key={img.id} style={{ border: '1px solid #ddd', padding: 8 }}>
                    <img
                      src={img.url}
                      alt='flyer'
                      style={{ width: '100%', height: 100, objectFit: 'cover' }}
                    />
                    <button type="button" onClick={() => handleDeleteFlyer(img.id)} style={{ marginTop: 6, width: '100%' }}>削除</button>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ color: '#666' }}>フライヤーはまだありません。</div>
            )}
            <div style={{ marginTop: 12 }}>
              <label>
                フライヤーを追加（複数可）：
                <input type="file" accept="image/*" multiple onChange={handleFlyerUpload} className={getFieldError('flyers') ? styles.inputError : ''} />
              </label>
              {getFieldError('flyers') && <p className={styles.fieldError}>{getFieldError('flyers')}</p>}
            </div>
          </fieldset>
          <fieldset>
            <legend>基本情報</legend>
            <label>
              イベント名：<input type="text" name="name" value={form.name} onChange={handleChange} className={getFieldError('name') ? styles.inputError : ''} required />
            </label>
            {getFieldError('name') && <p className={styles.fieldError}>{getFieldError('name')}</p>}
            <label>
              ジャンル：
              <input
                type="text"
                name="genre"
                value={form.genre}
                onChange={handleChange}
                list="event-genre-options"
                placeholder="候補から選択、または自由入力"
                className={getFieldError('genre') ? styles.inputError : ''}
              />
              <datalist id="event-genre-options">
                {EVENT_GENRE_OPTIONS.map((genre) => (
                  <option key={genre} value={genre} />
                ))}
              </datalist>
            </label>
            {getFieldError('genre') && <p className={styles.fieldError}>{getFieldError('genre')}</p>}
            <label>
              現在のslug（変更不可）：
              <input type="text" name="slug" value={form.slug || ''} readOnly className={getFieldError('slug') ? styles.inputError : ''} />
              {getFieldError('slug') && <p className={styles.fieldError}>{getFieldError('slug')}</p>}
              <span style={{ fontSize: '12px', color: '#666', display: 'block', marginTop: '2px' }}>
                ※イベントURLに使用されます。作成後は変更できません。
              </span>
            </label>
            <label>
              会場名（自由入力可）：<input type="text" name="venue_name" value={form.venue_name} onChange={handleChange} className={getFieldError('venue_name') ? styles.inputError : ''} />
            </label>
            {getFieldError('venue_name') && <p className={styles.fieldError}>{getFieldError('venue_name')}</p>}
            <label>
              住所：<input type="text" name="address" value={form.address} onChange={handleChange} className={getFieldError('address') ? styles.inputError : ''} required />
            </label>
            {getFieldError('address') && <p className={styles.fieldError}>{getFieldError('address')}</p>}
          </fieldset>
          <fieldset>
            <legend>日時</legend>
            <label>
              開催日：<input type="date" name="date" value={form.date} onChange={handleChange} className={getFieldError('date') ? styles.inputError : ''} disabled required />
            </label>
            {getFieldError('date') && <p className={styles.fieldError}>{getFieldError('date')}</p>}
            <label>
              終了日：<input type="date" name="end_date" value={form.end_date} onChange={handleChange} className={getFieldError('end_date') ? styles.inputError : ''} disabled required />
            </label>
            {getFieldError('end_date') && <p className={styles.fieldError}>{getFieldError('end_date')}</p>}
            <label>
              開場時間：<input type="time" name="open_time" value={form.open_time} onChange={handleChange} className={getFieldError('open_time') ? styles.inputError : ''} required />
            </label>
            {getFieldError('open_time') && <p className={styles.fieldError}>{getFieldError('open_time')}</p>}
            <label>
              開演時間：<input type="time" name="start_time" value={form.start_time} onChange={handleChange} className={getFieldError('start_time') ? styles.inputError : ''} required />
            </label>
            {getFieldError('start_time') && <p className={styles.fieldError}>{getFieldError('start_time')}</p>}
            <label>
              終演時間：<input type="time" name="end_time" value={form.end_time} onChange={handleChange} className={getFieldError('end_time') ? styles.inputError : ''} required />
            </label>
            {getFieldError('end_time') && <p className={styles.fieldError}>{getFieldError('end_time')}</p>}
          </fieldset>
          {/* イベント全体販売枚数フィールド削除（グループ単位へ移行） */}
          <fieldset>
            <legend>イベント種別・投げ銭設定</legend>
            {isPastEvent(form.date, form.end_date) && (
              <p style={{ color: '#b45309', fontSize: '13px', marginBottom: '8px' }}>
                ※ 過去のイベントは無料・投げ銭なしに固定されます。
              </p>
            )}
            <label>
              イベント種別：
              <select
                name="event_type"
                value={form.event_type}
                onChange={handleChange}
                disabled={isPastEvent(form.date, form.end_date)}
              >
                <option value="paid">有料</option>
                <option value="free">無料</option>
              </select>
            </label>

            <div style={{ marginTop: '12px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  name="donation_enabled"
                  checked={!!form.donation_enabled}
                  disabled={isPastEvent(form.date, form.end_date)}
                  onChange={(e) => setForm(prev => ({ ...prev, donation_enabled: e.target.checked }))}
                />
                投げ銭機能を有効にする
              </label>
            </div>

            {form.donation_enabled && (
              <div style={{ marginTop: '12px', paddingLeft: '8px', borderLeft: '3px solid #c7d2fe' }}>
                <label>
                  投げ銭受付開始日時（空欄でイベント開始と同時）：
                  <input
                    type="datetime-local"
                    name="donation_start_at"
                    value={form.donation_start_at}
                    onChange={handleChange}
                  />
                </label>
                <div style={{ marginTop: '8px', display: 'block', color: '#444' }}>
                  投げ銭受付終了日時：イベント終了後5日に自動設定されます（編集不可）。
                </div>
              </div>
            )}
          </fieldset>
          <fieldset>
            <legend>チケット種別・料金</legend>
            {form.event_type === 'free' ? (
              <p style={{ color: '#16a34a', fontWeight: '600' }}>
                ✅ 無料イベントのため、チケット種別の設定は不要です。
              </p>
            ) : (
              <>
            <label>
              決済手数料の負担：
              <select name="fee_bearer" value={form.fee_bearer} onChange={handleChange} className={getFieldError('fee_bearer') ? styles.inputError : ''} disabled>
                <option value="organizer">主催者負担</option>
                <option value="buyer">購入者負担</option>
              </select>
              {getFieldError('fee_bearer') && <p className={styles.fieldError}>{getFieldError('fee_bearer')}</p>}
              <span style={{ fontSize: '12px', color: '#888', display: 'block', marginTop: '2px' }}>
                ※この項目は登録後に変更できません。
              </span>
            </label>
            {ticketPrices.map((tp, idx) => {
              const now = new Date();
              const isNewGroup = !tp.id; // 新規追加のグループは常に編集可能（開始前扱い）
              let saleStarted = false;
              let saleActive = false;
              let saleEnded = false;
              if (!isNewGroup) {
                // 開始可否は日時入力値ではなく、実販売開始（売上/予約実績）で判定
                saleStarted = !!tp.hasActualSales;
                const endAt = tp.price_end_date ? new Date(tp.price_end_date) : null;
                saleActive = saleStarted && (!endAt || now <= endAt);
                saleEnded = saleStarted && !!endAt && now > endAt;
              }
              return (
              <div key={idx} className={styles.ticketGroupCard}>
                <div className={styles.ticketGroupHeader}>
                  <strong>種別ブロック {idx + 1}</strong>
                  <button type="button" onClick={() => removeTicketPrice(idx)} className={styles.dangerButton} disabled={saleActive || saleEnded}>削除</button>
                </div>

                <label>
                  種別：
                  <select
                    value={tp.type === '' ? 'その他' : (['超早期購入','早期購入','通常購入'].includes(tp.type) ? tp.type : 'その他')}
                    onChange={e => {
                      if (e.target.value === 'その他') {
                        handleTicketPriceChange(idx, 'type', '');
                      } else {
                        handleTicketPriceChange(idx, 'type', e.target.value);
                      }
                    }}
                    disabled={saleActive || saleEnded}
                  >
                    <option value="">選択</option>
                    <option value="超早期購入">超早期購入</option>
                    <option value="早期購入">早期購入</option>
                    <option value="通常購入">通常購入</option>
                    <option value="その他">その他</option>
                  </select>
                  {(tp.type === '' || !['超早期購入','早期購入','通常購入'].includes(tp.type)) && (
                    <>
                      <input
                        type="text"
                        value={tp.type}
                        maxLength={30}
                        onChange={e => {
                          const val = e.target.value;
                          if (/[<>]/.test(val)) return;
                          handleTicketPriceChange(idx, 'type', val);
                        }}
                        placeholder="種別を入力"
                            className={styles.inlineTypeInput}
                        disabled={saleActive || saleEnded}
                      />
                      {tp.type.length > 0 && /[<>]/.test(tp.type) && (
                            <span className={styles.inlineErrorText}>※「&lt;」「&gt;」は使用できません</span>
                      )}
                    </>
                  )}
                </label>
                  <label>
                    販売上限枚数(種別単位)：
                    <input
                      type="number"
                      value={tp.ticket_limit}
                      onChange={e => handleTicketPriceChange(idx, 'ticket_limit', e.target.value)}
                      placeholder="空欄で無制限"
                      disabled={saleEnded}
                    />
                  </label>
                <div className={styles.ticketMetaGrid}>
                  <label>
                    販売開始日時：
                    <input type="datetime-local" value={tp.price_date} onChange={e => handleTicketPriceChange(idx, 'price_date', e.target.value)} disabled={saleActive || saleEnded} />
                  </label>
                  <label>
                    販売終了日時：
                    <input type="datetime-local" value={tp.price_end_date} onChange={e => handleTicketPriceChange(idx, 'price_end_date', e.target.value)} disabled={saleEnded} />
                  </label>
                </div>
                <div className={styles.priceTypeArea}>
                  {tp.prices.map((pt, j) => (
                    <div key={j} className={styles.priceTypeRow}>
                      <label>
                        価格タイプ：
                        <select
                          value={pt.price_type === '' ? 'other' : (['通常','男性','女性','グループ','VIP'].includes(pt.price_type) ? pt.price_type : 'other')}
                          onChange={e => {
                            if (e.target.value === 'other') {
                              handlePriceTypeChange(idx, j, 'price_type', '');
                            } else {
                              handlePriceTypeChange(idx, j, 'price_type', e.target.value);
                            }
                          }}
                          disabled={saleActive || saleEnded}
                        >
                          <option value="通常">通常</option>
                          <option value="男性">男性</option>
                          <option value="女性">女性</option>
                          <option value="グループ">グループ</option>
                          <option value="VIP">VIP</option>
                          <option value="other">その他</option>
                        </select>
                        {(pt.price_type === '' || !['通常','男性','女性','グループ','VIP'].includes(pt.price_type)) && (
                          <>
                            <input
                              type="text"
                              value={pt.price_type}
                              maxLength={20}
                              onChange={e => {
                                const val = e.target.value;
                                if (/[<>]/.test(val)) return;
                                handlePriceTypeChange(idx, j, 'price_type', val);
                              }}
                              placeholder="価格タイプを入力"
                              className={styles.inlineTypeInput}
                              disabled={saleActive || saleEnded}
                            />
                            {pt.price_type.length > 0 && /[<>]/.test(pt.price_type) && (
                              <span className={styles.inlineErrorText}>※「&lt;」「&gt;」は使用できません</span>
                            )}
                          </>
                        )}
                      </label>
                      <label>
                        金額：
                        <input type="number" value={pt.price} onChange={e => handlePriceTypeChange(idx, j, 'price', e.target.value)} placeholder="円" disabled={saleActive || saleEnded} />
                      </label>
                      {pt.price_type === 'グループ' && (
                        <label>
                          グループ人数：
                          <input type="number" value={pt.min_people || ''} onChange={e => handlePriceTypeChange(idx, j, 'min_people', e.target.value)} placeholder="例: 4" disabled={saleActive || saleEnded} />
                        </label>
                      )}
                      <label>
                        販売上限枚数(価格タイプ単位)：
                        <input type="number" value={pt.ticket_limit} onChange={e => handlePriceTypeChange(idx, j, 'ticket_limit', e.target.value)} placeholder="空欄で無制限" disabled={saleEnded} />
                      </label>
                      <div className={styles.priceTypeActions}>
                        <button type="button" onClick={() => removePriceType(idx, j)} className={styles.dangerButton} disabled={saleActive || saleEnded}>価格タイプ削除</button>
                      </div>
                    </div>
                  ))}
                  <div className={styles.priceTypeActions}>
                    <button type="button" onClick={() => addPriceType(idx, '')} className={styles.secondaryButton} disabled={saleActive || saleEnded}>+ 価格タイプを追加</button>
                  </div>
                </div>
              </div>
            );})}
            <button type="button" onClick={addTicketPrice} className={styles.secondaryButton}>+ 種別を追加</button>
              </>
            )}
          </fieldset>
          <fieldset>
            <legend>出演アーティスト</legend>
            <div className={styles.artistGuideBox}>
              <strong>登録済みアーティスト:</strong> 検索して追加するとプロフィールと連携されます。<br />
              <strong>未登録アーティスト:</strong> 名前を手入力すると、このイベント専用の写真と説明を設定できます。
            </div>
            <label>
              アーティスト検索：
              <input
                type="text"
                value={artistSearch}
                onChange={(e) => setArtistSearch(e.target.value)}
                placeholder="出演アーティストを検索"
              />
            </label>
            <p className={styles.artistHint}>候補をクリックすると「選択中」にセットされます。</p>
            {artistSearch && artistResults.length > 0 && (
              <ul className={styles.searchResults}>
                {artistResults.map((a) => (
                  <li
                    key={a.id}
                    onClick={() => setSelectedArtist({ id: a.id, name: a.organization_name })}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className={styles.searchResultRow}>
                      <span>{a.organization_name}</span>
                      <button
                        type="button"
                        className={styles.searchAddButton}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          addArtistToList({ id: a.id, name: a.organization_name });
                          setSelectedArtist(null);
                          setArtistSearch('');
                          setArtistResults([]);
                        }}
                      >
                        追加
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
            {selectedArtist && (
              <div className={styles.selectedArtistBox}>
                <p className={styles.selectedArtistText}>選択中: {selectedArtist.name}</p>
                <button
                  className={styles.secondaryButton}
                  type="button"
                  onClick={() => {
                    addArtistToList(selectedArtist);
                    setSelectedArtist(null);
                    setArtistSearch('');
                    setArtistResults([]);
                  }}
                >
                  + 選択中のアーティストを追加
                </button>
              </div>
            )}
            {artists.map((artist, i) => (
              <div key={i} className={styles.artistRow}>
                <label className={styles.artistRowLabel}>
                  <span className={styles.artistRowHeader}>
                    <span className={styles.artistRowTitle}>出演者 {i + 1}</span>
                    <span className={`${styles.artistModeBadge} ${artist.id ? styles.artistModeRegistered : styles.artistModeManual}`}>
                      {artist.id ? '登録済み' : '手入力'}
                    </span>
                  </span>
                  <input
                    type="text"
                    value={artist.name}
                    onChange={(e) => handleArtistValueChange(i, 'name', e.target.value)}
                    placeholder={artist.id ? '登録済みアーティスト名' : '未登録アーティスト名を入力'}
                  />
                </label>
                <button
                  className={styles.dangerButton}
                  type="button"
                  onClick={() => {
                    const updated = [...artists];
                    updated.splice(i, 1);
                    setArtists(updated);
                  }}
                >
                  削除
                </button>
                {!artist.id && artist.name.trim() !== '' && (
                  <div className={styles.artistExtraFields}>
                    <div className={styles.artistExtraHint}>
                      このアーティストは未登録です。イベント詳細で表示する写真と説明を設定できます。
                    </div>
                    <label className={styles.artistRowLabel}>
                      <span className={styles.artistRowTitle}>アーティスト説明</span>
                      <textarea
                        value={artist.description || ''}
                        onChange={(e) => handleArtistValueChange(i, 'description', e.target.value)}
                        placeholder="未登録アーティストの説明を入力"
                        maxLength={2000}
                      />
                    </label>
                    <label className={styles.artistRowLabel}>
                      <span className={styles.artistRowTitle}>アーティスト写真</span>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleArtistImageChange(i, e.target.files?.[0] || null)}
                      />
                    </label>
                    {(artist.imagePreview || artist.existingImageUrl) && (
                      <img
                        src={artist.imagePreview || artist.existingImageUrl}
                        alt={`${artist.name || 'artist'} preview`}
                        className={styles.artistImagePreview}
                      />
                    )}
                  </div>
                )}
                {!artist.id && artist.name.trim() === '' && (
                  <div className={styles.artistExtraCollapsed}>
                    名前を入力すると、未登録アーティスト用の写真と説明の設定欄が表示されます。
                  </div>
                )}
              </div>
            ))}
            <div className={styles.artistActionRow}>
              <button
                className={styles.secondaryButton}
                type="button"
                onClick={() => setArtists([...artists, createEmptyArtist()])}
              >
                + アーティスト欄を追加
              </button>
            </div>
          </fieldset>
          <fieldset>
            <legend>イベントの説明</legend>
            <span style={{ fontSize: '12px', color: '#888', display: 'block', marginBottom: '4px' }}>
              ※3000文字以内。<br />
              <span style={{ color: '#888' }}>
                ※&lt; や &gt; の記号、絵文字は入力できません。文章のみ入力してください。
              </span>
            </span>
            <textarea
              name="description"
              value={form.description}
              onChange={handleChange}
              placeholder="イベント内容、注意事項など"
              maxLength={3000}
              autoComplete="off"
              className={getFieldError('description') ? styles.inputError : ''}
            />
            {getFieldError('description') && <p className={styles.fieldError}>{getFieldError('description')}</p>}
          </fieldset>

          <fieldset className={styles.fieldset}>
            <legend className={styles.legend}>問い合わせ先</legend>
            <span style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '4px' }}>
              参加者からの問い合わせ先を必ず記載してください。
            </span>
            <textarea
              name="contact_info"
              value={form.contact_info}
              onChange={handleChange}
              placeholder="メールアドレス、電話番号、SNSアカウントなど"
              maxLength={1000}
              autoComplete="off"
              required
              className={getFieldError('contact_info') ? styles.inputError : ''}
            />
            {getFieldError('contact_info') && <p className={styles.fieldError}>{getFieldError('contact_info')}</p>}
          </fieldset>
          <div className={styles.submitArea}>
            {getErrorSummary() && <p className={styles.error}>{getErrorSummary()}</p>}
            {success && <p style={{ color: '#16a34a', margin: '0 0 8px' }}>{success}</p>}
            <button type="submit" disabled={isSubmitting}>イベントを更新する</button>
            {getFieldError('submit') && <p className={styles.fieldError}>{getFieldError('submit')}</p>}
          </div>
        </form>
      </div>
    </>
  );
};

export default EventEditPage;
